import is from '@sindresorhus/is';
import type { UseMutationResult } from '@tanstack/react-query';
import { useMutation, useMutationState, useQueryClient } from '@tanstack/react-query';

import { useApplicationFetchers } from '../../fetchers/use-application-fetchers';
import { chatKeys } from './query-keys';

type DeleteChatThreadVariables = { readonly threadId: string };

const DELETE_CHAT_THREAD_MUTATION_KEY = [...chatKeys.all, 'delete-thread'] as const;

export const useDeleteChatThread = (): UseMutationResult<unknown, Error, DeleteChatThreadVariables> => {
    const queryClient = useQueryClient();
    const { deleteChatThread } = useApplicationFetchers();

    return useMutation({
        mutationFn: async ({ threadId }) => {
            const result = await deleteChatThread(threadId);

            if (result.isErr === true) {
                throw result.error;
            }

            return result.value;
        },
        mutationKey: DELETE_CHAT_THREAD_MUTATION_KEY,
        onSuccess: (_, { threadId }) => {
            queryClient.invalidateQueries({ queryKey: chatKeys.threads() });
            queryClient.removeQueries({ queryKey: chatKeys.thread(threadId) });
        },
    });
};

/** Whether a delete for this thread is still in flight, so its row can show it is going away. */
export const useIsDeletingChatThread = (threadId: string): boolean =>
    useMutationState({
        filters: { mutationKey: DELETE_CHAT_THREAD_MUTATION_KEY, status: 'pending' },
        select: (mutation) => mutation.state.variables,
    }).some((variables) => is.plainObject(variables) && variables['threadId'] === threadId);
